import { useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Skeleton } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { day, money } from '../lib/format.ts';
import { useAction } from './manage/common.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';
import { TaxReturnView } from './TaxReturn.tsx';

interface RateAmounts {
  rate: number;
  net: number;
  tax: number;
}
interface SalesRow {
  kind: 'receipts' | 'invoice' | 'credit';
  date: string;
  number: string;
  count: number;
  partner: string | null;
  tax_id: string | null;
  rates: RateAmounts[];
  net: number;
  tax: number;
  total: number;
  id: string | null;
}
interface PurchaseRow {
  id: string;
  date: string;
  number: number;
  supplier_ref: string | null;
  partner: string;
  tax_id: string | null;
  rate: number | null;
  net: number;
  tax: number;
  total: number;
}
interface VatBooksData {
  month: string;
  from: string;
  to: string;
  sales: SalesRow[];
  purchases: PurchaseRow[];
  summary: {
    sales: { rates: RateAmounts[]; net: number; tax: number; total: number };
    purchases: { rates: RateAmounts[]; mixed: { net: number; tax: number }; net: number; tax: number; total: number };
    due: number;
  };
  ledger: { on_sales: number; on_purchases: number; matches: boolean };
}

const pct = (r: number) => `${r} %`;
const salesDoc = (s: SalesRow) =>
  s.kind === 'receipts' ? tn('vat.receipts', s.count, { numbers: s.number }) : `${s.kind === 'credit' ? t('inv.cn_prefix') : t('inv.inv_prefix')} ${s.number}`;
const billName = (p: PurchaseRow) => [`${t('vat.bill')} ${p.number}`, p.supplier_ref].filter(Boolean).join(' · ');
const party = (name: string | null, taxId: string | null) =>
  name ? (
    <span className="vat-party">
      {name}
      {taxId ? <span className="muted tabular">{taxId}</span> : null}
    </span>
  ) : (
    <span className="muted">{t('vat.retail')}</span>
  );

/** The month's sales and purchase books, the VAT by rate and what is to pay. */
export function VatBooksTab({ initialMonth, version }: { initialMonth: string; version: number }) {
  const [month, setMonth] = useState(initialMonth);
  const [view, setView] = useState<'books' | 'return'>('books');
  const switcher = (
    <div className="segmented" role="radiogroup" aria-label={t('tr.view')}>
      {(['books', 'return'] as const).map((v) => (
        <button key={v} role="radio" aria-checked={view === v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
          <Icon name={v === 'books' ? 'book' : 'invoice'} size={16} />
          {t(`tr.view_${v}`)}
        </button>
      ))}
    </div>
  );
  const { data } = useAction<VatBooksData>('accounting.vat_books', { month, v: version });
  const b = data && data.month === month ? data : null;
  const salesCsv = () =>
    b &&
    downloadCsv(
      `${t('vat.file_sales')}-${month}.csv`,
      [t('acc.c_date'), t('vat.c_document'), t('vat.c_buyer'), t('vat.c_nipt'), t('vat.c_net'), t('vat.c_vat'), t('vat.c_total')],
      b.sales.map((s) => [s.date, salesDoc(s), s.partner ?? t('vat.retail'), s.tax_id ?? '', plain(s.net), plain(s.tax), plain(s.total)]),
    );
  const purchasesCsv = () =>
    b &&
    downloadCsv(
      `${t('vat.file_purchases')}-${month}.csv`,
      [t('acc.c_date'), t('vat.c_document'), t('vat.c_supplier'), t('vat.c_nipt'), t('vat.c_rate'), t('vat.c_net'), t('vat.c_vat'), t('vat.c_total')],
      b.purchases.map((p) => [p.date, billName(p), p.partner, p.tax_id ?? '', p.rate === null ? t('vat.mixed') : pct(p.rate), plain(p.net), plain(p.tax), plain(p.total)]),
    );
  if (view === 'return') return <TaxReturnView month={month} onMonth={setMonth} switcher={switcher} />;
  return (
    <div className="rep-grid">
      <div className="toolbar">
        {switcher}
        <label className="vat-month">
          <span>{t('vat.month')}</span>
          <input className="input date-input" type="month" value={month} onChange={(e) => /^\d{4}-\d{2}$/.test(e.target.value) && setMonth(e.target.value)} />
        </label>
      </div>
      {!b ? (
        <>
          <Skeleton h={112} r="var(--radius-lg)" />
          <Skeleton h={300} r="var(--radius-lg)" />
        </>
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('vat.title')}>
            <Kpi label={t('acc.vat_sales')} value={money(b.summary.sales.tax)} hint={t('vat.on_net', { net: money(b.summary.sales.net) })} />
            <Kpi label={t('acc.vat_purchases')} value={money(b.summary.purchases.tax)} hint={t('vat.on_net', { net: money(b.summary.purchases.net) })} />
            <Kpi label={b.summary.due >= 0 ? t('acc.vat_due') : t('vat.carry')} value={money(Math.abs(b.summary.due))} hint={b.summary.due >= 0 ? t('vat.due_hint') : t('vat.carry_hint')} />
          </section>

          <p className={`acc-check rep-wide ${b.ledger.matches ? 'ok' : 'off'}`} role="status">
            <Icon name={b.ledger.matches ? 'checkCircle' : 'alert'} size={16} />
            {b.ledger.matches
              ? t('vat.ledger_ok')
              : t('vat.ledger_off', { sales: money(b.ledger.on_sales), purchases: money(b.ledger.on_purchases) })}
          </p>

          <section className="panel" aria-labelledby="vat-rates-sales">
            <div className="panel-head">
              <h2 id="vat-rates-sales">{t('vat.by_rate_sales')}</h2>
            </div>
            <RateTable rows={b.summary.sales.rates} />
          </section>
          <section className="panel" aria-labelledby="vat-rates-buy">
            <div className="panel-head">
              <h2 id="vat-rates-buy">{t('vat.by_rate_purchases')}</h2>
            </div>
            <RateTable rows={b.summary.purchases.rates} mixed={b.summary.purchases.mixed} />
          </section>

          <section className="panel rep-wide" aria-labelledby="vat-sales">
            <div className="panel-head">
              <h2 id="vat-sales">{t('vat.sales_book')}</h2>
              <Button variant="ghost" icon="download" onClick={salesCsv} disabled={!b.sales.length}>
                {t('rep.download')}
              </Button>
            </div>
            <Table
              head={[t('acc.c_date'), t('vat.c_document'), t('vat.c_buyer'), t('vat.c_net'), t('vat.c_vat'), t('vat.c_total')]}
              phoneHide={[2, 3, 5]}
              left={[1, 2]}
              empty={t('vat.no_sales')}
              rows={b.sales.map((s, i) => ({
                key: s.id ?? `r${i}`,
                cells: [
                  <span className="tabular">{day(s.date)}</span>,
                  s.kind === 'receipts' ? (
                    <span className="vat-party">
                      {tn('vat.receipts_n', s.count)}
                      <span className="muted tabular">{s.number}</span>
                    </span>
                  ) : (
                    <span className={s.kind === 'credit' ? 'vat-credit' : undefined}>{salesDoc(s)}</span>
                  ),
                  party(s.partner, s.tax_id),
                  <span className="tabular">{money(s.net)}</span>,
                  <span className="tabular">{money(s.tax)}</span>,
                  <span className="tabular strong">{money(s.total)}</span>,
                ],
              }))}
              foot={
                b.sales.length
                  ? [t('acc.total'), '', '', <span className="tabular strong">{money(b.summary.sales.net)}</span>, <span className="tabular strong">{money(b.summary.sales.tax)}</span>, <span className="tabular strong">{money(b.summary.sales.total)}</span>]
                  : null
              }
            />
          </section>

          <section className="panel rep-wide" aria-labelledby="vat-purchases">
            <div className="panel-head">
              <h2 id="vat-purchases">{t('vat.purchase_book')}</h2>
              <Button variant="ghost" icon="download" onClick={purchasesCsv} disabled={!b.purchases.length}>
                {t('rep.download')}
              </Button>
            </div>
            <Table
              head={[t('acc.c_date'), t('vat.c_document'), t('vat.c_supplier'), t('vat.c_rate'), t('vat.c_net'), t('vat.c_vat'), t('vat.c_total')]}
              phoneHide={[1, 3, 4, 6]}
              left={[1, 2]}
              empty={t('vat.no_purchases')}
              rows={b.purchases.map((p) => ({
                key: p.id,
                cells: [
                  <span className="tabular">{day(p.date)}</span>,
                  billName(p),
                  party(p.partner, p.tax_id),
                  p.rate === null ? <span className="badge badge-warn">{t('vat.mixed')}</span> : <span className="tabular">{pct(p.rate)}</span>,
                  <span className="tabular">{money(p.net)}</span>,
                  <span className="tabular">{money(p.tax)}</span>,
                  <span className="tabular strong">{money(p.total)}</span>,
                ],
              }))}
              foot={
                b.purchases.length
                  ? [t('acc.total'), '', '', '', <span className="tabular strong">{money(b.summary.purchases.net)}</span>, <span className="tabular strong">{money(b.summary.purchases.tax)}</span>, <span className="tabular strong">{money(b.summary.purchases.total)}</span>]
                  : null
              }
            />
            {b.purchases.some((p) => !p.tax_id) ? <p className="muted small vat-note">{t('vat.no_nipt')}</p> : null}
          </section>
          <p className="muted small rep-wide">{t('vat.hint')}</p>
        </>
      )}
    </div>
  );
}

function RateTable({ rows, mixed }: { rows: RateAmounts[]; mixed?: { net: number; tax: number } }) {
  const all = [...rows.map((r) => ({ key: String(r.rate), label: t('vat.rate_n', { rate: r.rate }), net: r.net, tax: r.tax }))];
  if (mixed && (mixed.net || mixed.tax)) all.push({ key: 'mixed', label: t('vat.mixed_rates'), net: mixed.net, tax: mixed.tax });
  return (
    <Table
      head={[t('vat.c_rate'), t('vat.c_net'), t('vat.c_vat')]}
      left={[0]}
      empty={t('vat.nothing')}
      rows={all.map((r) => ({ key: r.key, cells: [r.label, <span className="tabular">{money(r.net)}</span>, <span className="tabular strong">{money(r.tax)}</span>] }))}
    />
  );
}
